import { FieldValues, FieldErrors } from 'react-hook-form';
import classNames from 'classnames';
import ValidationMessage from 'elements/ValidationMessage';
import { Field } from 'elements/Field';
import TextareaAutosize, {
  TextareaAutosizeProps,
} from 'react-textarea-autosize';
import { useTranslations } from 'next-intl';
import { fieldErrorMessage } from 'elements/ValidationMessage/fieldErrorMessage';

// Extends TextareaAutosizeProps so all native props (value, onChange, etc.)
// are automatically supported via ...rest spread
interface Props extends Omit<
  TextareaAutosizeProps,
  'className' | 'minRows' | 'maxRows'
> {
  name: string;
  label: string;
  errors?: FieldErrors<FieldValues>;
  hideLabel?: boolean;
  className?: string;
  labelClassName?: string;
  textareaClassName?: string;
  // For the box around the textarea, see `Field.Root`.
  rootClassName?: string;
  minRows?: number;
  maxRows?: number;
}

// A multi-line text field that grows with its content: label, the field
// box, and the error under it.
const Textarea: React.FC<Props> = ({
  name,
  label,
  errors = {},
  placeholder = '',
  hideLabel = false,
  className,
  labelClassName,
  textareaClassName,
  rootClassName,
  readOnly = false,
  disabled = false,
  minRows = 1,
  maxRows = 8,
  ...rest
}) => {
  const t = useTranslations();
  const hasError = Boolean(errors?.[name]);

  return (
    <div className={className}>
      <Field.Label
        className={hideLabel ? 'sr-only' : labelClassName}
        htmlFor={name}
      >
        {label}
      </Field.Label>

      <Field.Root
        invalid={hasError}
        disabled={disabled}
        className={rootClassName}
      >
        <Field.Input
          as={TextareaAutosize}
          id={name}
          name={name}
          minRows={minRows}
          maxRows={maxRows}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={disabled}
          aria-invalid={hasError ? 'true' : 'false'}
          className={classNames('resize-none', textareaClassName)}
          {...rest}
        />
      </Field.Root>

      {hasError && (
        <ValidationMessage className={styles.validationMessage}>
          {fieldErrorMessage(errors[name], t)}
        </ValidationMessage>
      )}
    </div>
  );
};

export default Textarea;

const styles = {
  validationMessage: 'pl-2',
};
